import React from "react";
import { Box, Button, Typography } from "@mui/material";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GrammarTransition {
  from: number;
  to: number;
  letter: string;
}

interface GrammarDef {
  initial: number;
  final: number[];
  transitions: GrammarTransition[];
}

export interface GrammarConfig {
  letters?: string[];
  string_length_range?: [number, number];
  training_phase?: {
    strings_per_grammar: number;
    display_time_ms: number;
    isi_ms: number;
    introduction?: {
      title?: string;
      text?: string;
      instructions?: string[];
    };
  };
  test_phase?: {
    total_trials: number;
    isi_ms: number;
    confidence_scale?: {
      min: number;
      max: number;
      label_min: string;
      label_max: string;
    };
    introduction?: {
      title?: string;
      text?: string;
      instructions?: string[];
    };
  };
  grammars?: Record<string, GrammarDef>;
}

export interface GrammarTrialResult {
  trial_number: number;
  string_shown: string;
  correct_grammar: string;
  user_response: string;
  is_correct: boolean;
  confidence: number;
  response_time_ms: number;
}

export interface GrammarTaskAnswer {
  training_phase: {
    strings_shown: Array<{ grammar: string; string: string }>;
  };
  test_phase: {
    correct_count: number;
    error_count: number;
    trials: GrammarTrialResult[];
  };
}

interface Props {
  onAnswerChange: (taskAnswer?: GrammarTaskAnswer) => void;
  grammarConfig?: Partial<GrammarConfig>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG: {
  letters: string[];
  string_length_range: [number, number];
  training_phase: { strings_per_grammar: number; display_time_ms: number; isi_ms: number };
  test_phase: {
    total_trials: number;
    isi_ms: number;
    confidence_scale: { min: number; max: number; label_min: string; label_max: string };
  };
  grammars: Record<string, GrammarDef>;
} = {
  letters: ["M", "T", "V", "R", "X"],
  string_length_range: [5, 9],
  training_phase: {
    strings_per_grammar: 6,
    display_time_ms: 2500,
    isi_ms: 1000,
  },
  test_phase: {
    total_trials: 10,
    isi_ms: 1000,
    confidence_scale: {
      min: 1,
      max: 5,
      label_min: "Chute completo",
      label_max: "Certeza total",
    },
  },
  grammars: {
    G1: {
      initial: 0,
      final: [3],
      transitions: [
        { from: 0, to: 1, letter: "M" },
        { from: 0, to: 1, letter: "X" },
        { from: 1, to: 1, letter: "X" },
        { from: 1, to: 2, letter: "R" },
        { from: 1, to: 2, letter: "V" },
        { from: 2, to: 1, letter: "V" },
        { from: 2, to: 3, letter: "M" },
        { from: 2, to: 3, letter: "T" },
        { from: 3, to: 3, letter: "M" },
      ],
    },
    G2: {
      initial: 0,
      final: [3],
      transitions: [
        { from: 0, to: 1, letter: "X" },
        { from: 0, to: 1, letter: "M" },
        { from: 0, to: 1, letter: "V" },
        { from: 1, to: 1, letter: "R" },
        { from: 1, to: 2, letter: "V" },
        { from: 1, to: 2, letter: "T" },
        { from: 2, to: 1, letter: "T" },
        { from: 2, to: 3, letter: "R" },
        { from: 2, to: 3, letter: "M" },
        { from: 3, to: 3, letter: "R" },
      ],
    },
  },
};

// ─── String generation ────────────────────────────────────────────────────────

function generateString(grammar: GrammarDef, minLen: number, maxLen: number): string | null {
  for (let attempt = 0; attempt < 500; attempt++) {
    let state = grammar.initial;
    let str = "";
    for (let step = 0; step < maxLen + 5; step++) {
      const currentState = state;
      const available = grammar.transitions.filter((t) => t.from === currentState);
      if (available.length === 0) break;
      let choices = available;
      if (str.length >= minLen) {
        const toFinal = available.filter((t) => grammar.final.includes(t.to));
        if (toFinal.length > 0 && Math.random() < 0.5) choices = toFinal;
      }
      const t = choices[Math.floor(Math.random() * choices.length)];
      str += t.letter;
      state = t.to;
      if (grammar.final.includes(state) && str.length >= minLen && str.length <= maxLen) return str;
      if (str.length >= maxLen) break;
    }
  }
  return null;
}

function generateStrings(grammar: GrammarDef, count: number, minLen: number, maxLen: number): string[] {
  const results = new Set<string>();
  let attempts = 0;
  while (results.size < count && attempts < 5000) {
    attempts++;
    const s = generateString(grammar, minLen, maxLen);
    if (s) results.add(s);
  }
  return Array.from(results).slice(0, count);
}

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function resolveConfig(raw?: Partial<GrammarConfig>): typeof DEFAULT_CONFIG {
  if (!raw) return DEFAULT_CONFIG;
  return {
    letters: raw.letters ?? DEFAULT_CONFIG.letters,
    string_length_range: raw.string_length_range ?? DEFAULT_CONFIG.string_length_range,
    training_phase: raw.training_phase ?? DEFAULT_CONFIG.training_phase,
    test_phase: {
      ...DEFAULT_CONFIG.test_phase,
      ...raw.test_phase,
      confidence_scale:
        raw.test_phase?.confidence_scale ?? DEFAULT_CONFIG.test_phase.confidence_scale,
    },
    grammars: raw.grammars ?? DEFAULT_CONFIG.grammars,
  };
}

// ─── Screen type ──────────────────────────────────────────────────────────────

type Screen =
  | "intro"
  | "training_isi"
  | "training"
  | "training_pause"
  | "test_intro"
  | "test_isi"
  | "test"
  | "confidence"
  | "done";

// ─── Component ────────────────────────────────────────────────────────────────

const GrammarQuestion: React.FC<Props> = ({ onAnswerChange, grammarConfig }) => {
  const cfg = React.useMemo(() => resolveConfig(grammarConfig), [grammarConfig]);

  // Generated strings (populated on mount)
  const g1StringsRef = React.useRef<string[]>([]);
  const g2StringsRef = React.useRef<string[]>([]);
  const testTrialsRef = React.useRef<Array<{ string: string; grammar: string }>>([]);

  // UI display state
  const [screen, setScreen] = React.useState<Screen>("intro");
  const [trainingGrammar, setTrainingGrammar] = React.useState("G1");
  const [trainingTotal, setTrainingTotal] = React.useState(0);
  const [trainingIdx, setTrainingIdx] = React.useState(0);
  const [testIdx, setTestIdx] = React.useState(0);
  const [testTotal, setTestTotal] = React.useState(0);
  const [displayString, setDisplayString] = React.useState("");
  const [progressPct, setProgressPct] = React.useState(1);
  const [userResponse, setUserResponse] = React.useState<string | null>(null);
  const [stringsReady, setStringsReady] = React.useState(false);
  const [finalAnswer, setFinalAnswer] = React.useState<GrammarTaskAnswer | undefined>();

  // Accumulated results
  const stringsShownRef = React.useRef<Array<{ grammar: string; string: string }>>([]);
  const trialsRef = React.useRef<GrammarTrialResult[]>([]);
  const pendingTrialRef = React.useRef<{
    string: string;
    grammar: string;
    response: string;
    correct: boolean;
    responseTimeMs: number;
  } | null>(null);
  const trialStartRef = React.useRef(0);
  const testIdxRef = React.useRef(0);

  // Timers
  const timersRef = React.useRef<number[]>([]);
  const clearTimers = React.useCallback(() => {
    timersRef.current.forEach((id) => {
      clearTimeout(id);
      clearInterval(id);
    });
    timersRef.current = [];
  }, []);
  const addTimer = React.useCallback(
    (id: number) => {
      timersRef.current.push(id);
    },
    []
  );

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  // Initialize strings on mount
  React.useEffect(() => {
    const [minLen, maxLen] = cfg.string_length_range;
    const grammarKeys = Object.keys(cfg.grammars);

    // Training strings: strings_per_grammar per grammar
    const trainingByGrammar: Record<string, string[]> = {};
    grammarKeys.forEach((key) => {
      trainingByGrammar[key] = generateStrings(
        cfg.grammars[key],
        cfg.training_phase.strings_per_grammar,
        minLen,
        maxLen
      );
    });

    // Test strings: total_trials distributed evenly across grammars
    const trialsPerGrammar = Math.ceil(cfg.test_phase.total_trials / grammarKeys.length);
    const testCombined: Array<{ string: string; grammar: string }> = [];
    grammarKeys.forEach((key) => {
      const strings = generateStrings(cfg.grammars[key], trialsPerGrammar, minLen, maxLen);
      strings.forEach((s) => testCombined.push({ string: s, grammar: key }));
    });
    const testTrials = shuffleArray(testCombined).slice(0, cfg.test_phase.total_trials);

    const g1Key = grammarKeys[0] ?? "G1";
    const g2Key = grammarKeys[1] ?? "G2";
    g1StringsRef.current = trainingByGrammar[g1Key] ?? [];
    g2StringsRef.current = trainingByGrammar[g2Key] ?? [];
    testTrialsRef.current = testTrials;
    setTrainingTotal(trainingByGrammar[g1Key]?.length ?? 0);
    setTestTotal(testTrials.length);
    setStringsReady(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Training flow ──────────────────────────────────────────────────────────

  const startTrainingStringRef = React.useRef<
    (grammar: string, idx: number, strings: string[]) => void
  >(() => { });

  const startTrainingString = React.useCallback(
    (grammar: string, idx: number, strings: string[]) => {
      clearTimers();
      if (idx >= strings.length) {
        const grammarKeys = Object.keys(cfg.grammars);
        const isFirstGrammar = grammar === (grammarKeys[0] ?? "G1");
        if (isFirstGrammar) {
          setScreen("training_pause");
        } else {
          setScreen("test_intro");
        }
        return;
      }

      const str = strings[idx];
      stringsShownRef.current.push({ grammar, string: str });
      setTrainingGrammar(grammar);
      setTrainingIdx(idx);
      setDisplayString(str);
      setProgressPct(1);
      setScreen("training");

      // Progress bar interval
      const startTime = performance.now();
      addTimer(
        window.setInterval(() => {
          const pct = Math.max(
            0,
            1 - (performance.now() - startTime) / cfg.training_phase.display_time_ms
          );
          setProgressPct(pct);
        }, 50) as unknown as number
      );

      // Advance after display_time_ms → ISI → next string
      addTimer(
        window.setTimeout(() => {
          clearTimers();
          setScreen("training_isi");
          addTimer(
            window.setTimeout(() => {
              const grammarKeys = Object.keys(cfg.grammars);
              const nextStrings =
                grammar === (grammarKeys[0] ?? "G1")
                  ? g1StringsRef.current
                  : g2StringsRef.current;
              startTrainingStringRef.current(grammar, idx + 1, nextStrings);
            }, cfg.training_phase.isi_ms) as unknown as number
          );
        }, cfg.training_phase.display_time_ms) as unknown as number
      );
    },
    [cfg.grammars, cfg.training_phase, clearTimers, addTimer]
  );

  startTrainingStringRef.current = startTrainingString;

  // ── Test flow ──────────────────────────────────────────────────────────────

  const startTestTrialRef = React.useRef<(idx: number) => void>(() => { });

  const startTestTrial = React.useCallback(
    (idx: number) => {
      clearTimers();
      testIdxRef.current = idx;
      setTestIdx(idx);
      setScreen("test_isi");
      addTimer(
        window.setTimeout(() => {
          const trial = testTrialsRef.current[idx];
          setDisplayString(trial.string);
          trialStartRef.current = performance.now();
          setScreen("test");
        }, cfg.test_phase.isi_ms) as unknown as number
      );
    },
    [cfg.test_phase.isi_ms, clearTimers, addTimer]
  );

  startTestTrialRef.current = startTestTrial;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleStartTraining = React.useCallback(() => {
    const grammarKeys = Object.keys(cfg.grammars);
    const firstKey = grammarKeys[0] ?? "G1";
    startTrainingStringRef.current(firstKey, 0, g1StringsRef.current);
  }, [cfg.grammars]);

  const handleContinueToSecondGrammar = React.useCallback(() => {
    const grammarKeys = Object.keys(cfg.grammars);
    const secondKey = grammarKeys[1] ?? "G2";
    startTrainingStringRef.current(secondKey, 0, g2StringsRef.current);
  }, [cfg.grammars]);

  const handleStartTest = React.useCallback(() => {
    trialsRef.current = [];
    startTestTrialRef.current(0);
  }, []);

  const handleTestResponse = React.useCallback(
    (response: string) => {
      if (screen !== "test") return;
      const responseTimeMs = Math.round(performance.now() - trialStartRef.current);
      const trial = testTrialsRef.current[testIdxRef.current];
      const isCorrect = response === trial.grammar;
      setUserResponse(response);
      pendingTrialRef.current = {
        string: trial.string,
        grammar: trial.grammar,
        response,
        correct: isCorrect,
        responseTimeMs,
      };
      setScreen("confidence");
    },
    [screen]
  );

  const handleConfidence = React.useCallback(
    (confidence: number) => {
      const data = pendingTrialRef.current!;
      const trialNum = testIdxRef.current + 1;
      trialsRef.current.push({
        trial_number: trialNum,
        string_shown: data.string,
        correct_grammar: data.grammar,
        user_response: data.response,
        is_correct: data.correct,
        confidence,
        response_time_ms: data.responseTimeMs,
      });

      const nextIdx = testIdxRef.current + 1;
      if (nextIdx >= testTrialsRef.current.length) {
        const correctCount = trialsRef.current.filter((t) => t.is_correct).length;
        const errorCount = trialsRef.current.filter((t) => !t.is_correct).length;
        const answer: GrammarTaskAnswer = {
          training_phase: { strings_shown: [...stringsShownRef.current] },
          test_phase: {
            correct_count: correctCount,
            error_count: errorCount,
            trials: [...trialsRef.current],
          },
        };
        setFinalAnswer(answer);
        setScreen("done");
        onAnswerChange(answer);
      } else {
        startTestTrialRef.current(nextIdx);
      }
    },
    [onAnswerChange]
  );

  // Keyboard shortcuts
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (screen === "training_pause" && e.code === "Space") {
        e.preventDefault();
        handleContinueToSecondGrammar();
      }
      if (screen === "test") {
        if (e.key === "1") { e.preventDefault(); handleTestResponse("G1"); }
        if (e.key === "2") { e.preventDefault(); handleTestResponse("G2"); }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [screen, handleContinueToSecondGrammar, handleTestResponse]);

  // ── Derived ────────────────────────────────────────────────────────────────

  const grammarKeys = Object.keys(cfg.grammars);
  const confidenceScale =
    cfg.test_phase.confidence_scale ?? DEFAULT_CONFIG.test_phase.confidence_scale;
  const confidenceValues = Array.from(
    { length: confidenceScale.max - confidenceScale.min + 1 },
    (_, i) => i + confidenceScale.min
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 760,
        boxSizing: "border-box",
        margin: "0 auto",
        mt: 4,
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        backgroundColor: "grey.100",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >

      {/* ── INTRO ─────────────────────────────────────────────────────────── */}
      {screen === "intro" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h5" color="primary" sx={{ textAlign: "center", fontWeight: 700 }}>
              {grammarConfig?.training_phase?.introduction?.title ?? "Gramática Artificial"}
            </Typography>
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              {grammarConfig?.training_phase?.introduction?.text ?? "Fase de memorização"}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Instruções
            </Typography>
            {grammarConfig?.training_phase?.introduction?.instructions != null ? (
              <Box component="ul" sx={{ listStyleType: "disc", paddingLeft: "1.25rem", margin: 0 }}>
                {grammarConfig.training_phase.introduction.instructions.map((line: string, i: number) => (
                  <Box component="li" key={i} sx={{ marginBottom: "0.3rem" }}>
                    <Typography variant="body2" color="text.secondary">{line}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Você participará de um teste de memória visual. Sua tarefa é observar e tentar memorizar as sequências de letras o máximo possível.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  • Cada sequência ficará visível por{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>{(cfg.training_phase.display_time_ms / 1000).toFixed(1)} segundos</Box>
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleStartTraining}
              disabled={!stringsReady}
            >
              Começar
            </Button>
          </Box>
        </Box>
      )}

      {/* ── TRAINING ISI ──────────────────────────────────────────────────── */}
      {screen === "training_isi" && (
        <Box sx={{ minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "grey.400" }} />
        </Box>
      )}

      {/* ── TRAINING ──────────────────────────────────────────────────────── */}
      {screen === "training" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" color="primary" fontWeight={700}>
              {trainingGrammar === (grammarKeys[0] ?? "G1") ? "Primeira" : "Segunda"} fase de memorização
            </Typography>
          </Box>

          <Box sx={{ height: 4, backgroundColor: "grey.300", borderRadius: 2, overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${progressPct * 100}%`,
                backgroundColor: "primary.main",
                transition: "width 0.05s linear",
              }}
            />
          </Box>

          <Box
            sx={{
              minHeight: 220,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
            }}
          >
            <Typography
              variant="h1"
              color="primary"
              sx={{ fontSize: { xs: "2.75rem", md: "4.5rem" }, letterSpacing: "0.25em", minHeight: "6rem", wordBreak: "break-word", textAlign: "center" }}
            >
              {displayString}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              memorize esta sequência
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── TRAINING PAUSE ────────────────────────────────────────────────── */}
      {screen === "training_pause" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h6" color="primary" sx={{ textAlign: "center", fontWeight: 700 }}>
              Pausa — primeira fase de memorização concluída
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 2 }}>
              Mais algumas sequências de letras aparecerão na tela. Novamente, sua tarefa é{" "}
              <strong>observar e memorizar</strong> as sequências o máximo possível.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Você está pronto(a) para continuar? Se sim, aperte a{" "}
              <strong>barra de espaço</strong> ou clique em Continuar.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button variant="contained" size="medium" onClick={handleContinueToSecondGrammar}>
              Continuar
            </Button>
          </Box>
        </Box>
      )}

      {/* ── TEST INTRO ────────────────────────────────────────────────────── */}
      {screen === "test_intro" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h5" color="primary" sx={{ textAlign: "center", fontWeight: 700 }}>
              {grammarConfig?.test_phase?.introduction?.title ?? "Tarefa"}
            </Typography>
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              {grammarConfig?.test_phase?.introduction?.text ?? (
                <>
                  As sequências exibidas na primeira fase de memorização seguem a
                  <Box component="span" sx={{ fontWeight: 700 }}> Regra Gramatical G1</Box>, enquanto as da segunda fase de memorização pertencem à
                  <Box component="span" sx={{ fontWeight: 700 }}> Regra Gramatical G2</Box>. A partir de agora, sua tarefa é determinar se as novas sequências obedecem à regra
                  <Box component="span" sx={{ fontWeight: 700 }}> G1 ou G2</Box>.
                </>
              )}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Instruções
            </Typography>
            {grammarConfig?.test_phase?.introduction?.instructions != null ? (
              <Box component="ul" sx={{ listStyleType: "disc", paddingLeft: "1.25rem", margin: 0 }}>
                {grammarConfig.test_phase.introduction.instructions.map((line: string, i: number) => (
                  <Box component="li" key={i} sx={{ marginBottom: "0.3rem" }}>
                    <Typography variant="body2" color="text.secondary">{line}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  • Sua tarefa é identificar a qual regra gramatical, <Box component="span" sx={{ fontWeight: 700 }}>G1 ou G2</Box>, cada sequência de letras pertence.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Após classificar cada sequência de letras como <Box component="span" sx={{ fontWeight: 700 }}>G1 ou G2</Box>, você deverá atribuir um grau de confiança à sua escolha. Utilize a escala de{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {confidenceScale.min} ({confidenceScale.label_min}) a{" "}
                    {confidenceScale.max} ({confidenceScale.label_max})
                  </Box> para indicar o quanto você acredita que sua resposta está correta.
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button variant="contained" size="medium" onClick={handleStartTest}>
              Começar
            </Button>
          </Box>
        </Box>
      )}

      {/* ── TEST ISI ──────────────────────────────────────────────────────── */}
      {screen === "test_isi" && (
        <Box sx={{ minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "grey.400" }} />
        </Box>
      )}

      {/* ── TEST ──────────────────────────────────────────────────────────── */}
      {screen === "test" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" color="primary" fontWeight={700}>
              Fase de classificação
            </Typography>
          </Box>

          <Box
            sx={{
              minHeight: 220,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
              gap: 2,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Qual é a regra gramatical desta sequência?
            </Typography>
            <Typography
              variant="h1"
              color="text.primary"
              sx={{ fontSize: { xs: "2.75rem", md: "4.5rem" }, letterSpacing: "0.25em", minHeight: "6rem", wordBreak: "break-word", textAlign: "center" }}
            >
              {displayString}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              {grammarKeys.map((g, i) => (
                <Button
                  key={g}
                  variant="outlined"
                  size="large"
                  onClick={() => handleTestResponse(g)}
                  sx={{ fontSize: 20, px: 5, py: 1.5, fontWeight: 700, minWidth: 100 }}
                >
                  {g}
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                    [{i + 1}]
                  </Typography>
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ── CONFIDENCE ────────────────────────────────────────────────────── */}
      {screen === "confidence" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" color="primary" sx={{ textAlign: "center" }}>
            Avalie sua confiança
          </Typography>

          <Box
            sx={{
              backgroundColor: "grey.100",
              borderRadius: "6px",
              padding: { xs: "1rem 1rem", md: "1.25rem 2rem" },
              textAlign: "center",
              wordBreak: "break-word",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Você respondeu:{" "}
              <strong>{userResponse}</strong>
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
              {confidenceValues.map((c) => (
                <Button
                  key={c}
                  variant="outlined"
                  onClick={() => handleConfidence(c)}
                  sx={{
                    width: 50,
                    height: 50,
                    minWidth: 50,
                    margin: "0 3px",
                    border: "1px solid #1ea097 !important",
                  }}
                >
                  <Typography variant="h5" color="primary">
                    {c}
                  </Typography>
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                maxWidth: 300,
                mx: "auto",
              }}
            >
              <Typography variant="caption" color="text.disabled">
                {confidenceScale.label_min}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {confidenceScale.label_max}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── DONE ──────────────────────────────────────────────────────────── */}
      {screen === "done" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, textAlign: "center", alignItems: "center" }}>
          <Typography variant="h5" color="primary">
            Tarefa concluída
          </Typography>
          <Typography variant="body1">Acertos: {finalAnswer?.test_phase.correct_count ?? 0}</Typography>
          <Typography variant="body1">Erros: {finalAnswer?.test_phase.error_count ?? 0}</Typography>
          <Typography variant="body2" color="text.secondary">
            Clique em "Próximo" para continuar.
          </Typography>
        </Box>
      )}

    </Box>
  );
};

export default GrammarQuestion;
